import { useEffect, useMemo, useState } from 'react'
import type { NoteType, WeekSummary } from '@shared/types'
import { get } from '../../lib/api'
import { formatDate, formatTime, pluralise, shiftDateKey, toDecimalHours } from '../../lib/format'
import { Button, Chip, SectionCard, Spinner, TableScroll } from '../../components/ui'

const SLOTS = 8
const OTHER = '__other__'
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

interface Series {
  id: string
  name: string
  color: string
}

/** "Sep 21 – 27", or "Sep 28 – Oct 4" across a month end. */
function weekLabel(from: string, to: string): string {
  const fmt = (d: string, opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', ...opts }).format(new Date(`${d}T12:00:00Z`))
  const sameMonth = from.slice(0, 7) === to.slice(0, 7)
  return `${fmt(from, { month: 'short', day: 'numeric' })} – ${fmt(to, sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' })}`
}

/** Clean hour ticks: 0 / 4 / 8 … sized to the tallest day. */
function ticksFor(maxHours: number): number[] {
  const step = maxHours <= 8 ? 2 : maxHours <= 24 ? 4 : maxHours <= 60 ? 10 : 20
  const top = Math.max(step, Math.ceil(maxHours / step) * step)
  return Array.from({ length: top / step + 1 }, (_, i) => i * step)
}

/**
 * Hours per site per day for one week, stacked. Tapping (or hovering, or
 * focusing) a segment shows that site's updates for that day underneath —
 * a phone has no hover, so the readout is a panel, not a floating tooltip.
 * The table below carries every number, so nothing depends on the chart.
 */
export function WeekChart({ timezone, noteTypes }: { timezone: string; noteTypes: NoteType[] }) {
  const [anchor, setAnchor] = useState<string | null>(null)
  const [data, setData] = useState<WeekSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [picked, setPicked] = useState<{ date: string; siteId: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void get<WeekSummary>(`/api/admin/week${anchor ? `?date=${anchor}` : ''}`)
      .then((res) => {
        if (cancelled) return
        setData(res)
        setPicked(null)
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [anchor])

  // Eight hues at most, in fixed order by site name; past eight, the sites
  // with the fewest hours fold into "Other" rather than a ninth made-up hue.
  const { series, seriesOf } = useMemo(() => {
    if (!data) return { series: [] as Series[], seriesOf: (id: string) => id }
    const totals = new Map<string, number>()
    for (const day of data.days) for (const s of day.sites) totals.set(s.siteId, (totals.get(s.siteId) ?? 0) + s.ms)
    const byName = [...data.sites]
    const kept = byName.length <= SLOTS
      ? byName
      : [...byName].sort((a, b) => (totals.get(b.id) ?? 0) - (totals.get(a.id) ?? 0)).slice(0, SLOTS - 1)
        .sort((a, b) => a.name.localeCompare(b.name))
    const keptIds = new Set(kept.map((s) => s.id))
    const list: Series[] = kept.map((s, i) => ({ id: s.id, name: s.name, color: `var(--wm-series-${i + 1})` }))
    if (kept.length < byName.length) list.push({ id: OTHER, name: 'Other sites', color: 'var(--wm-series-other)' })
    return { series: list, seriesOf: (id: string) => (keptIds.has(id) ? id : OTHER) }
  }, [data])

  if (!data) {
    return (
      <SectionCard title="This week">
        <div className="py-8"><Spinner /></div>
      </SectionCard>
    )
  }

  const hours = (ms: number) => ms / 3_600_000
  const dayStacks = data.days.map((day) => {
    const bySeries = new Map<string, number>()
    for (const s of day.sites) bySeries.set(seriesOf(s.siteId), (bySeries.get(seriesOf(s.siteId)) ?? 0) + s.ms)
    // Bottom-up in legend order, so a site sits at the same height every day.
    const segments = series.filter((s) => bySeries.has(s.id)).map((s) => ({ series: s, ms: bySeries.get(s.id)! }))
    return { date: day.date, segments, total: segments.reduce((n, seg) => n + seg.ms, 0) }
  })
  const maxHours = Math.max(0, ...dayStacks.map((d) => hours(d.total)))
  const ticks = ticksFor(maxHours)
  const top = ticks[ticks.length - 1]
  const PLOT = 176

  const siteName = new Map(data.sites.map((s) => [s.id, s.name]))
  const pickedNotes = picked
    ? data.notes.filter((n) => n.date === picked.date && n.siteId && seriesOf(n.siteId) === picked.siteId)
    : []
  const pickedStack = picked ? dayStacks.find((d) => d.date === picked.date) : null
  const pickedSeg = pickedStack?.segments.find((seg) => seg.series.id === picked?.siteId)

  // Table rows: every site (even ones folded into "Other" in the chart).
  const rows = data.sites.map((site) => {
    const perDay = data.days.map((day) => day.sites.find((s) => s.siteId === site.id)?.ms ?? 0)
    return { site, perDay, total: perDay.reduce((a, b) => a + b, 0) }
  })
  const dayTotals = data.days.map((day) => day.sites.reduce((n, s) => n + s.ms, 0))
  const weekTotal = dayTotals.reduce((a, b) => a + b, 0)
  const typeOf = (key: string) => noteTypes.find((t) => t.key === key)

  return (
    <SectionCard
      title="Hours by site"
      action={
        <div className="flex items-center gap-1">
          <Button className="min-h-9 px-2 text-xs" onClick={() => setAnchor(shiftDateKey(data.from, -7))}>‹</Button>
          <span className="font-mono text-xs font-semibold whitespace-nowrap tnum">{weekLabel(data.from, data.to)}</span>
          <Button className="min-h-9 px-2 text-xs" onClick={() => setAnchor(shiftDateKey(data.from, 7))}>›</Button>
        </div>
      }
    >
      <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        {weekTotal === 0 ? (
          <p className="text-sm text-graphite-2">No hours logged this week.</p>
        ) : (
          <>
            {/* Legend: always present, rect swatches to mirror the bars. */}
            <ul className="mb-3 flex flex-wrap gap-x-4 gap-y-1">
              {series.map((s) => (
                <li key={s.id} className="flex items-center gap-1.5 text-xs text-graphite">
                  <span className="size-2.5 shrink-0 rounded-[2px]" style={{ background: s.color }} aria-hidden />
                  {s.name}
                </li>
              ))}
            </ul>

            <div className="flex gap-2">
              {/* Y axis */}
              <div className="relative w-6 shrink-0" style={{ height: PLOT }} aria-hidden>
                {ticks.map((t) => (
                  <span
                    key={t}
                    className="absolute right-0 -translate-y-1/2 font-mono text-[10px] text-graphite-2 tnum"
                    style={{ top: PLOT - (t / top) * PLOT }}
                  >
                    {t}
                  </span>
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <div className="relative" style={{ height: PLOT }}>
                  {ticks.map((t) => (
                    <div
                      key={t}
                      className="absolute inset-x-0 h-px bg-line"
                      style={{ top: PLOT - (t / top) * PLOT }}
                      aria-hidden
                    />
                  ))}
                  <div className="absolute inset-0 flex items-end justify-around">
                    {dayStacks.map((day, dayIndex) => (
                      <div key={day.date} className="flex h-full w-6 flex-col-reverse items-stretch gap-[2px]">
                        {day.segments.map((seg, i) => {
                          const isTop = i === day.segments.length - 1
                          const active = picked?.date === day.date && picked.siteId === seg.series.id
                          const label = `${seg.series.name}, ${DAY_LABELS[dayIndex]}: ${toDecimalHours(seg.ms)} hours`
                          return (
                            <button
                              key={seg.series.id}
                              type="button"
                              aria-label={label}
                              aria-pressed={active}
                              title={label}
                              // Always select, never toggle: a tap focuses the
                              // segment first (which selects it), so a toggle
                              // here would immediately undo the tap.
                              onClick={() => setPicked({ date: day.date, siteId: seg.series.id })}
                              onPointerEnter={(e) => {
                                if (e.pointerType === 'mouse') setPicked({ date: day.date, siteId: seg.series.id })
                              }}
                              onFocus={() => setPicked({ date: day.date, siteId: seg.series.id })}
                              className={`relative w-full shrink-0 ${isTop ? 'rounded-t-[4px]' : ''} ${
                                picked && !active ? 'opacity-45' : ''
                              }`}
                              style={{
                                height: Math.max(2, (hours(seg.ms) / top) * PLOT - 2),
                                background: seg.series.color,
                              }}
                            >
                              {/* Hit area wider than the 24px mark. */}
                              <span className="absolute inset-y-0 -inset-x-2" aria-hidden />
                            </button>
                          )
                        })}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-1 flex justify-around border-t border-line-strong pt-1">
                  {dayStacks.map((day, i) => (
                    <div key={day.date} className="w-10 text-center">
                      <p className="text-[10px] font-bold text-graphite-2 uppercase">{DAY_LABELS[i]}</p>
                      <p className="font-mono text-[10px] text-graphite tnum">
                        {day.total ? toDecimalHours(day.total) : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-3 min-h-10 border-t border-line pt-3" aria-live="polite">
              {!picked || !pickedSeg ? (
                <p className="text-xs text-graphite-2">Tap a bar to see that site’s updates for the day.</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm">
                    <strong className="font-mono tnum">{toDecimalHours(pickedSeg.ms)} h</strong>{' '}
                    <span className="text-graphite-2">
                      · {pickedSeg.series.name} · {formatDate(`${picked.date}T12:00:00Z`, 'UTC')}
                    </span>
                  </p>
                  {pickedNotes.length === 0 ? (
                    <p className="text-xs text-graphite-2">No updates posted from this site that day.</p>
                  ) : pickedNotes.map((note) => {
                    const type = typeOf(note.type)
                    return (
                      <div key={note.id} className="border-l-3 border-line-strong pl-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Chip color={type?.color ?? '#6B6560'}>{type?.label ?? note.type}</Chip>
                          <span className="font-mono text-[10px] text-graphite-2 tnum">
                            {formatTime(note.occurredAt, timezone)}
                          </span>
                          <span className="text-xs font-bold">{note.userName}</span>
                          {picked.siteId === OTHER && note.siteId && (
                            <span className="text-xs text-graphite-2">{siteName.get(note.siteId)}</span>
                          )}
                        </div>
                        <p className="mt-0.5 line-clamp-3 text-xs whitespace-pre-wrap">
                          {note.body ?? <span className="text-graphite-2">{pluralise(note.photoCount, 'photo')}</span>}
                        </p>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {weekTotal > 0 && (
        <TableScroll>
          <table className="grid-table min-w-max">
            <thead>
              <tr>
                <th>Site</th>
                {DAY_LABELS.map((d) => <th key={d} className="text-right">{d}</th>)}
                <th className="text-right">Week</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.site.id}>
                  <td className="font-semibold">{row.site.name}</td>
                  {row.perDay.map((ms, i) => (
                    <td key={i} className="text-right font-mono text-graphite-2 tnum">{ms ? toDecimalHours(ms) : '–'}</td>
                  ))}
                  <td className="text-right font-mono font-semibold tnum">{toDecimalHours(row.total)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-graphite">
                <td className="font-bold">Total</td>
                {dayTotals.map((ms, i) => (
                  <td key={i} className="text-right font-mono tnum">{ms ? toDecimalHours(ms) : '–'}</td>
                ))}
                <td className="text-right font-mono font-bold tnum">{toDecimalHours(weekTotal)}</td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
      )}
    </SectionCard>
  )
}
